import { useEffect } from 'react';
import { useAuthStore } from '../features/authentication/store/authStore';
import { useContextStore } from '../stores/contextStore';
import { useSubjectStore } from '../stores/subjectStore';
import { useCurrentRoleContext } from '../features/role-context';
import { useActiveRole } from './useActiveRole';

/**
 * Context Manager Hook
 * Automatically manages active context based on user authentication and role context
 * Now uses the role context system for precise context management
 */
export const useContextManager = () => {
  const { userSession, isAuthenticated } = useAuthStore();
  const { setActiveContext, clearActiveContext } = useContextStore();
  const setActiveSubjectId = useSubjectStore((state) => state.setActiveSubjectId);

  // Get current role context from client-side storage
  const { data: currentRoleContext, error: roleContextError } = useCurrentRoleContext();

  useEffect(() => {
    if (!isAuthenticated) {
      // User is not authenticated, clear all context
      clearActiveContext();
      return;
    }

    if (!userSession) {
      // No user session available, clear context
      clearActiveContext();
      return;
    }

    if (roleContextError) {
      // Fallback to basic tenant context
      const tenantId = userSession.currentTenantId;
      if (tenantId) {
        setActiveContext({ tenantId });
      } else {
        clearActiveContext();
      }
      return;
    }

    if (currentRoleContext) {
      // Set complete context hierarchy from role context
      setActiveContext({
        tenantId: currentRoleContext.tenantId,
        domainId: currentRoleContext.domainId,
        subjectId: currentRoleContext.subjectId,
      });

      // Update subject store for backward compatibility
      if (currentRoleContext.subjectId) {
        setActiveSubjectId(currentRoleContext.subjectId);
      }
    } else {
      // No role context available, use basic tenant context
      const tenantId = userSession.currentTenantId;
      if (tenantId) {
        setActiveContext({ tenantId });
      } else {
        clearActiveContext();
      }
    }
  }, [
    isAuthenticated,
    userSession,
    currentRoleContext,
    roleContextError,
    setActiveContext,
    clearActiveContext,
    setActiveSubjectId,
  ]);
};

/**
 * Helper hook to set domain context
 */
export const useSetDomainContext = () => {
  const { setActiveDomainId } = useContextStore();
  return setActiveDomainId;
};

/**
 * Helper hook to set subject context
 */
export const useSetSubjectContext = () => {
  const { setActiveSubjectId } = useContextStore();
  return setActiveSubjectId;
};

/**
 * Helper hook to get current context
 */
export const useCurrentContext = () => {
  const { activeTenantId, activeDomainId, activeSubjectId } = useContextStore();

  return {
    tenantId: activeTenantId,
    domainId: activeDomainId,
    subjectId: activeSubjectId,
  };
};

/**
 * Helper to determine if user needs to select additional context
 * Based on their roles, they might need to select domain and/or subject
 */
export const useContextRequirements = () => {
  const { userSession } = useAuthStore();
  const { activeTenantId, activeDomainId, activeSubjectId } = useContextStore();
  const { isSystemAdmin, isUniversityAdmin, isInstituteAdmin, isLecturer, isCourseRepresentative } =
    useActiveRole();

  if (!userSession?.currentRoles) {
    return {
      needsTenant: !activeTenantId,
      needsDomain: false,
      needsSubject: false,
    };
  }

  // Use active role flags (role context aware)
  const hasSystemAdmin = isSystemAdmin;
  const hasTenantAdmin = isUniversityAdmin;
  const hasInstituteAdmin = isInstituteAdmin;
  const hasLecturer = isLecturer;
  const hasCourseRep = isCourseRepresentative;

  // System admins don't need any specific context
  if (hasSystemAdmin) {
    return {
      needsTenant: false,
      needsDomain: false,
      needsSubject: false,
    };
  }

  // Tenant admins only need tenant context (which should always be set)
  if (hasTenantAdmin && !hasInstituteAdmin && !hasLecturer && !hasCourseRep) {
    return {
      needsTenant: !activeTenantId,
      needsDomain: false,
      needsSubject: false,
    };
  }

  // Institute admins need tenant and domain context
  if (hasInstituteAdmin && !hasLecturer && !hasCourseRep) {
    return {
      needsTenant: !activeTenantId,
      needsDomain: !activeDomainId,
      needsSubject: false,
    };
  }

  // Lecturers and course representatives need all context levels
  if (hasLecturer || hasCourseRep) {
    return {
      needsTenant: !activeTenantId,
      needsDomain: !activeDomainId,
      needsSubject: !activeSubjectId,
    };
  }

  // Default: require tenant context
  return {
    needsTenant: !activeTenantId,
    needsDomain: false,
    needsSubject: false,
  };
};
